import {Component, Input, OnInit} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {MembershipService} from '../../../../../../@shared/services/membership-service';
import {MembershipPlanResponse, CompanyResponse} from '../../../../../../@shared/models/response.module';
import {_AQH_MEM_6} from '../../../../../../@shared/models/input.module';
import {ToastService} from '../../../../../../@core/services/toast.service';
import {TranslateService} from '@ngx-translate/core';

@Component({
  selector: 'app-edit-membership-plan',
  standalone: false,
  templateUrl: './edit-membership-plan.html',
  styleUrl: './edit-membership-plan.css'
})
export class EditMembershipPlan implements OnInit {
  @Input() plan: MembershipPlanResponse;
  @Input() company: CompanyResponse;

  name: string = '';
  description: string = '';
  coverage: string = '';
  billing_cycle: string = '';
  price_cents: number = 0;
  currency: string = 'NOK';
  max_children: number | null = null;
  benefits: string[] = [];
  newBenefit: string = '';
  saving: boolean = false;
  submitted: boolean = false;

  coverageOptions: {value: string, display: string}[] = [];
  billingCycleOptions: {value: string, display: string}[] = [];

  currencyOptions = [
    { code: 'NOK', name: 'NOK' },
    { code: 'SEK', name: 'SEK' },
    { code: 'EUR', name: 'EUR' },
    { code: 'USD', name: 'USD' },
    { code: 'GBP', name: 'GBP' },
    { code: 'DKK', name: 'DKK' },
  ];

  constructor(private membershipService: MembershipService,
              private modal: NgbActiveModal,
              private toastService: ToastService,
              private translate: TranslateService) {
  }

  // Validation helpers
  get isNameInvalid(): boolean {
    return this.submitted && !this.name.trim();
  }

  get isCoverageInvalid(): boolean {
    return this.submitted && !this.coverage;
  }

  get isBillingCycleInvalid(): boolean {
    return this.submitted && !this.billing_cycle;
  }

  get isPriceInvalid(): boolean {
    return this.submitted && (this.price_cents === null || this.price_cents === undefined || this.price_cents < 0);
  }

  get isFormValid(): boolean {
    return !!(this.name.trim() && this.coverage && this.billing_cycle && this.price_cents >= 0);
  }

  ngOnInit() {
    // Initialize translated options
    this.coverageOptions = [
      {value: 'individual', display: this.translate.instant('companyProfile.membershipPlans.coverage.individual')},
      {value: 'family', display: this.translate.instant('companyProfile.membershipPlans.coverage.family')}
    ];
    this.billingCycleOptions = [
      {value: 'annual', display: this.translate.instant('companyProfile.membershipPlans.billingCycle.annual')},
      {value: 'monthly', display: this.translate.instant('companyProfile.membershipPlans.billingCycle.monthly')}
    ];

    // Initialize form values from plan
    this.name = this.plan.name;
    this.description = this.plan.description || '';
    this.coverage = this.plan.coverage;
    this.billing_cycle = this.plan.billing_cycle;
    this.price_cents = this.plan.price_cents / 100; // Convert cents to dollars
    this.currency = this.plan.currency || this.company?.default_currency || 'NOK';
    this.max_children = this.plan.max_children;
    this.benefits = this.plan.benefits ? [...this.plan.benefits] : [];
  }

  addBenefit() {
    if (this.newBenefit.trim()) {
      this.benefits.push(this.newBenefit.trim());
      this.newBenefit = '';
    }
  }

  removeBenefit(index: number) {
    this.benefits.splice(index, 1);
  }

  onSave() {
    this.submitted = true;

    if (!this.isFormValid) {
      this.toastService.error(this.translate.instant('validation.fillRequiredFields'));
      return;
    }

    this.saving = true;
    const params: _AQH_MEM_6 = {
      plan_id: this.plan.plan_id,
      name: this.name.trim(),
      description: this.description || undefined,
      coverage: this.coverage,
      billing_cycle: this.billing_cycle,
      price_cents: this.price_cents * 100, // Convert dollars to cents
      currency: this.currency,
      max_children: this.coverage === 'family' ? (this.max_children ?? undefined) : undefined,
      benefits: this.benefits
    };

    this.membershipService.updateMembershipPlan(params).subscribe({
      next: () => {
        this.saving = false;
        this.toastService.success(this.translate.instant('companyProfile.membershipPlans.editPlan.success'));
        this.modal.close(true);
      },
      error: (err) => {
        this.saving = false;
        this.toastService.error(err.error?.reason || this.translate.instant('companyProfile.membershipPlans.editPlan.error'));
      }
    });
  }

  close() {
    this.modal.close();
  }

  /**
   * Calculate net amount after platform + payment fees
   * Stripe: 2.4% + 2.00 NOK, Platform: 2%
   * Net = price × 0.956 - 2.00
   */
  calculateNetAmount(price: number): number {
    if (!price || price <= 0) return 0;
    const net = price * 0.956 - 2;
    return Math.max(0, Math.round(net * 100) / 100);
  }
}
